'use client';

export const dynamic = 'force-dynamic';
// export const revalidate = false; // Disable caching

import Head from 'next/head';
import { useEffect, useState } from 'react';

import { EnhancedSchedulingFlow } from '@/components/simple-scheduler/EnhancedSchedulingFlow';

/**
 * Page component for embedding the scheduler in an iframe
 */
export default function SchedulerApiEmbed({
  searchParams,
}: {
  searchParams: { [key: string]: string };
}) {
  // Add state to store manually parsed parameters
  const [manualParams, setManualParams] = useState<{ [key: string]: string }>(
    {}
  );

  // Debug logging for parameters
  console.log('=== EMBED PAGE INITIALIZATION ===');
  console.log(
    'Full URL:',
    typeof window !== 'undefined' ? window.location.href : 'SSR Mode'
  );
  console.log(
    'All Search Params:',
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).toString()
      : Object.entries(searchParams)
          .map(([k, v]) => `${k}=${v}`)
          .join('&')
  );
  console.log('searchParams object:', searchParams);
  console.log('searchParams empty?', Object.keys(searchParams).length === 0);
  console.log('API Key from params:', searchParams.apiKey || 'MISSING');
  console.log('Team ID from params:', searchParams.teamId || 'MISSING');
  console.log(
    'Referrer:',
    typeof document !== 'undefined' ? document.referrer : 'SSR Mode'
  );
  console.log('===============================');

  // Parse URL parameters manually if searchParams is empty (happens in production)
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      Object.keys(searchParams).length === 0
    ) {
      console.log('SearchParams empty, parsing URL manually');
      const urlParams = new URLSearchParams(window.location.search);
      const extractedParams: { [key: string]: string } = {};

      // Extract all parameters
      for (const [key, value] of urlParams.entries()) {
        extractedParams[key] = value;
      }

      console.log('Manually extracted params:', extractedParams);
      setManualParams(extractedParams);
    }
  }, [searchParams]);

  // Use either searchParams or manually extracted params
  const effectiveParams =
    Object.keys(searchParams).length > 0 ? searchParams : manualParams;
  const { teamId, primaryColor, apiKey, logoUrl } = effectiveParams;

  console.log('Using effective params:', effectiveParams);
  console.log(
    'Using teamId:',
    teamId,
    'apiKey:',
    apiKey?.substring(0, 8) + '...',
    'logoUrl:',
    logoUrl
  );

  const [color, setColor] = useState('#e05d44');
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [isValidApiKey, setIsValidApiKey] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // Validate API key on mount
  useEffect(() => {
    const validateApiKey = async () => {
      if (!apiKey) {
        console.error('we have no api key');
        console.error(effectiveParams);
        console.error(apiKey);
        console.error(teamId);
        console.error(primaryColor);
        setErrorMessage('API key is required');
        setIsLoading(false);
        return;
      }

      try {
        // Get the parent domain from the referrer if available
        const parentDomain = document.referrer
          ? new URL(document.referrer).hostname
          : window.location.hostname;

        console.log('Validation context:', {
          apiKey,
          parentDomain,
          currentDomain: window.location.hostname,
          referrer: document.referrer,
        });

        const response = await fetch(
          `/api/simple-scheduler/validate-api-key?apiKey=${apiKey}&cacheBust=${Date.now()}`,
          {
            headers: {
              'X-API-Key': apiKey,
              'X-Test-Domain': parentDomain,
              Origin: document.referrer || window.location.origin,
              Referer: document.referrer || window.location.href,
            },
            // Ensure it's not cached
            cache: 'no-store',
          }
        );

        if (!response.ok) {
          const data = await response.json();
          console.error('API key validation failed:', {
            status: response.status,
            data,
            headers: Object.fromEntries(response.headers.entries()),
          });
          setErrorMessage(
            data.error || 'Invalid API key or unauthorized domain'
          );
          setIsValidApiKey(false);
        } else {
          const data = await response.json();
          // Add detailed logging of the exact response structure
          console.log('API key validation succeeded:', {
            fullResponse: JSON.stringify(data),
            hasTeamId: !!data.teamId,
            hasTeam_id: !!data.team_id,
            teamIdType: typeof data.teamId,
            team_idType: typeof data.team_id,
            requestedTeamId: teamId,
          });

          // Handle both camelCase and snake_case formats
          const validTeamId = data.teamId || data.team_id;

          if (
            validTeamId &&
            (!teamId || parseInt(teamId) !== parseInt(validTeamId))
          ) {
            console.log(
              `Using team ID ${validTeamId} from API key instead of ${teamId}`,
              {
                validTeamIdType: typeof validTeamId,
                parsedValidTeamId: parseInt(validTeamId),
                teamIdType: typeof teamId,
                parsedTeamId: parseInt(teamId || '0'),
              }
            );
          }

          // Check if we actually have a valid team ID
          if (!validTeamId) {
            console.error('No team ID found in response:', data);
            setErrorMessage('Invalid response format: missing team ID');
            setIsValidApiKey(false);
          } else {
            setIsValidApiKey(true);
          }
        }
      } catch (error) {
        console.error('Error validating API key:', error);
        setErrorMessage('Failed to validate API key');
      } finally {
        setIsLoading(false);
      }
    };

    validateApiKey();
  }, [apiKey, teamId, effectiveParams]);

  // Handle primary color from URL params
  useEffect(() => {
    if (typeof primaryColor === 'string') {
      // Validate the color value (should be a valid hex color)
      const isValidColor = /^#([0-9A-F]{3}){1,2}$/i.test(primaryColor);
      if (isValidColor) {
        setColor(primaryColor);
      }
    }
  }, [primaryColor]);

  // Listen for messages from parent window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'scheduler:addCloseButton') {
        setShowCloseButton(true);
      }

      // Handle dropoff tracking request from parent
      if (event.data && event.data.type === 'request_dropoff_data') {
        try {
          // Get phone number if available
          const phoneInput = document.querySelector(
            'input[type="tel"]'
          ) as HTMLInputElement;
          const phone = phoneInput?.value || '';

          // Get current stage/step heading if available
          const heading =
            document.querySelector('h2')?.textContent || 'unknown';

          // Send data back to parent
          if (window.parent) {
            window.parent.postMessage(
              {
                type: 'dropoff_data_response',
                data: {
                  phone_number: phone,
                  company_id: teamId,
                  stage: heading,
                },
              },
              '*'
            );
          }
        } catch (error) {
          console.error('Error preparing dropoff data:', error);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [teamId]);

  // Handle close button click
  const handleClose = () => {
    if (window.parent) {
      window.parent.postMessage({ type: 'scheduler:close' }, '*');
    }
  };

  // Handle successful scheduling
  const handleComplete = (data: any) => {
    // Send a message to the parent window
    if (window.parent) {
      window.parent.postMessage({ type: 'scheduler:complete', data }, '*');
    }
  };

  // Add global styles to remove extra spacing
  useEffect(() => {
    // Add a style element to remove any default margins/padding
    const style = document.createElement('style');
    style.textContent = `
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        height: auto !important;
        background-color: white !important;
        font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif !important;
      }

      /* Add proper spacing for the content */
      .scheduler-container {
        padding: 0.75rem;
        box-sizing: border-box;
      }

      /* Improve button styling */
      button {
        font-family: inherit;
      }

      /* Improve form elements */
      input, select, textarea {
        font-family: inherit;
        border-radius: 0.375rem;
        border: 1px solid #d1d5db;
        padding: 0.5rem 0.75rem;
        font-size: 1rem;
        line-height: 1.5;
      }

      /* Style headings */
      h1, h2, h3, h4, h5, h6 {
        color: #111827;
        font-weight: 600;
        line-height: 1.25;
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
      }

      /* Reduce spacing between elements */
      p, div {
        margin-top: 0.25rem;
        margin-bottom: 0.25rem;
      }

      /* Make options fill more space */
      [role="button"], .option-button {
        width: 100%;
        margin-bottom: 0.5rem;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Loading...</h2>
      </div>
    );
  }

  if (!isValidApiKey) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Invalid API Key</h2>
        <p>{errorMessage}</p>
      </div>
    );
  }

  return (
    <div
      className='scheduler-container'
      style={{
        margin: 0,
        padding: '0.75rem',
        height: 'auto',
        overflow: 'auto',
        backgroundColor: 'white',
        borderRadius: '0.5rem',
      }}
    >
      <Head>
        <title>Schedule an Appointment</title>
        <meta name='robots' content='noindex' />
        <meta name='viewport' content='width=device-width, initial-scale=1.0' />
      </Head>

      {/* Close button */}
      {showCloseButton && (
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            fontSize: '20px',
            cursor: 'pointer',
            zIndex: 10,
            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label='Close scheduling widget'
        >
          ×
        </button>
      )}

      {teamId && isValidApiKey && (
        <EnhancedSchedulingFlow
          companyId={teamId.toString()}
          primaryColor={color}
          onComplete={handleComplete}
          apiKey={apiKey}
          logoUrl={logoUrl}
          enableAnalytics={true}
        />
      )}
      {!teamId && isValidApiKey && (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Invalid Configuration</h2>
          <p>
            The scheduler widget is not properly configured. Please contact
            support.
          </p>
        </div>
      )}
    </div>
  );
}
